import React, { useEffect, useRef, useState } from "react";
import "./VerifyEmail.css";
import "./Login.css";
import LoginLogo from "../../Components/Assets/images/logo2.png";
import { Link, useNavigate } from "react-router-dom";
import { account, tablesDB, DB_ID, USERS_TABLE } from "../../appwrite";
import { useTitle } from "../../hooks/useTitle";
import { CheckCircle, Loader } from "lucide-react";

function VerifyEmail() {
  useTitle("Verify Email - TrendBay Seller");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentOk, setResentOk] = useState(false);
  const [done, setDone] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    account.get()
      .then((u) => { setUser(u); sendOtp(u); })
      .catch(() => navigate("/login"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const WORKER = "https://email-api.trendbayds.com";

  const sendOtp = async (u) => {
    if (!u) return;
    setResending(true);
    setResentOk(false);
    setError("");
    try {
      const otp = String(Math.floor(100000 + Math.random() * 900000));
      const otpExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      await tablesDB.updateRow({
        databaseId: DB_ID, tableId: USERS_TABLE, rowId: u.$id,
        data: { verifyOtp: otp, verifyOtpExpiry: otpExpiry },
      });
      fetch(`${WORKER}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "email_verification",
          payload: { email: u.email, fullName: u.name || "there", otp },
        }),
      }).catch(() => {});
      setResentOk(true);
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      setCooldown(30);
    } catch {
      setError("Failed to send code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleChange = (idx, val) => {
    const d = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = d;
    setDigits(next);
    setError("");
    if (d && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (text.length === 6) {
      setDigits(text.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const otp = digits.join("");
    if (otp.length < 6) { setError("Enter the full 6-digit code."); return; }
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${WORKER}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.$id, otp }),
      });
      const data = await res.json();
      if (!data.ok) { setError(data.error || "Verification failed."); return; }
      await account.deleteSession("current");
      setDone(true);
    } catch {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => sendOtp(user);

  if (done) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: "center" }}>
          <a href="/"><img className="auth-logo" src={LoginLogo} alt="TrendBay" /></a>
          <div className="auth-badge">🏪 Seller Portal</div>
          <div className="verify-email-icon">
            <CheckCircle size={36} color="#00A855" strokeWidth={1.5} />
          </div>
          <h1 className="auth-heading">Email verified!</h1>
          <p className="auth-sub">Your seller account is ready. Sign in to your dashboard.</p>
          <Link
            to="/login"
            className="auth-btn-primary"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", marginTop: 8 }}
          >
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ textAlign: "center" }}>
        <a href="/"><img className="auth-logo" src={LoginLogo} alt="TrendBay" /></a>
        <div className="auth-badge">🏪 Seller Portal</div>
        <h1 className="auth-heading">Check your email</h1>
        <p className="auth-sub" style={{ maxWidth: 320, margin: "0 auto 20px" }}>
          We sent a 6-digit code to <strong>{user?.email || "your email"}</strong>. It expires in 15 minutes.
        </p>

        {error && <div className="auth-error">{error}</div>}
        {resentOk && <div className="auth-success">New code sent! Check your inbox.</div>}

        <div
          style={{ display: "flex", gap: 8, justifyContent: "center", margin: "16px 0" }}
          onPaste={handlePaste}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              style={{
                width: 44, height: 54, textAlign: "center", fontSize: 24, fontWeight: 700,
                border: `2px solid ${d ? "#00A855" : "#e0e0e0"}`, borderRadius: 10,
                outline: "none", fontFamily: "Inter, sans-serif", color: "#080F0D",
                background: d ? "#f0fdf6" : "#fafafa", transition: "border-color 0.15s",
              }}
            />
          ))}
        </div>

        <button
          className="auth-btn-primary"
          style={{ width: "100%", marginBottom: 10 }}
          onClick={handleVerify}
          disabled={loading || digits.join("").length < 6}
        >
          {loading ? <Loader size={15} className="spin" style={{ display: "inline" }} /> : "Verify Email"}
        </button>

        <button
          className="auth-btn-outline"
          style={{ width: "100%" }}
          onClick={handleResend}
          disabled={resending || cooldown > 0}
        >
          {resending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>

        <div className="auth-divider" style={{ marginTop: 16 }}>or</div>
        <button
          className="auth-btn-outline"
          onClick={async () => { await account.deleteSession("current").catch(() => {}); navigate("/login"); }}
        >
          Go to Sign In
        </button>
      </div>
    </div>
  );
}

export default VerifyEmail;
